import { createContext, useContext, useMemo, useState, useEffect } from "react";
import translations from "../i18n/translations";

const LanguageContext = createContext(null);
const STORAGE_KEY = "virsa_language";
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "hi", label: "हिंदी" },
];

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return SUPPORTED_LANGUAGES.some(l => l.code === stored) ? stored : "en";
    } catch {
      return "en";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // Private browsing / storage disabled — language just won't persist.
    }
  }, [language]);

  // Cross-tab sync — same fix as ThemeContext.jsx's, for the same reason:
  // the `storage` event only fires on OTHER tabs of the same origin, never
  // the one that made the write. Without this, changing language in one
  // tab left every other already-open tab showing whatever it started with.
  useEffect(() => {
    function onStorage(e) {
      if (e.key !== STORAGE_KEY) return;
      if (SUPPORTED_LANGUAGES.some(l => l.code === e.newValue)) {
        setLanguage(e.newValue);
      }
    }
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Falls back to English, then to the key itself, so an untranslated
  // string (most of the app, for now) never renders blank.
  const t = useMemo(() => {
    return key => translations[language]?.[key] ?? translations.en[key] ?? key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
